import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactComponent as SalaryLogo } from '../../../assets/images/integrations/salary.svg'
import { useEmberTransitionTo } from '../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { useSalaryConnectionStatus } from '../../salaryIntegration'
import { IntegrationStatusLabel } from '../elements/IntegrationCard/elements/IntegrationStatusLabel'
import { IntegrationId } from '../enums/integrationId'
import { IntegrationStatus } from '../enums/integrationStatus'
import { IntegrationItem } from '../types/integrationItem'

export const useSalaryIntegration = (): IntegrationItem => {
  const { t } = useTranslation()
  const { transitionTo } = useEmberTransitionTo()
  const { url: salaryUrl } = useOrganizationViewUrl(reactRoute.organization.salary.getMainRoute())
  const { isSalaryAvailable, isLoading: isSalaryLoading, isConnected: isSalaryConnected } = useSalaryConnectionStatus()

  const integrationStatus = useMemo(() => {
    if (isSalaryLoading) {
      return null
    }

    return isSalaryConnected ? IntegrationStatus.Active : IntegrationStatus.Inactive
  }, [isSalaryLoading, isSalaryConnected])

  const buttonHandler = useCallback(() => {
    salaryUrl && transitionTo(salaryUrl)
  }, [salaryUrl, transitionTo])

  const buttonText = useMemo(() => {
    if (integrationStatus === IntegrationStatus.Inactive) {
      return t('integrations.card.salary.button.activate')
    } else if (integrationStatus === IntegrationStatus.Active) {
      return t('integrations.card.salary.button.deactivate')
    }
  }, [integrationStatus, t])

  return {
    id: IntegrationId.Salary,
    name: t('integrations.card.salary.title'),
    description: t('integrations.card.salary.body'),
    readMoreLink: 'https://www.billy.dk/apps/salary/',
    imageNode: <SalaryLogo />,
    isHidden: !isSalaryAvailable,
    isHiddenLoading: isSalaryLoading,
    buttonHandler,
    buttonText,
    statusLabelNode: <IntegrationStatusLabel status={integrationStatus} isStatusLoading={isSalaryLoading} />,
    isStatusLoading: isSalaryLoading,
  }
}
